import React from 'react'
import axios from 'axios'
import * as yup from 'yup'
import { Grid, Icon, Button, Pagination } from '@softwaredev_cegeka/stix-core'
import {
    Fieldset,
    FormGroup,
    Input,
    Info,
    Textarea,
} from '@softwaredev_cegeka/stix-form'

import { AuthApi } from 'src/api/authApi'
import BaseForm from 'src/components/Forms/BaseForm'
import AsyncSelect from 'src/components/Forms/AsyncSelect'
import { authenticatedUserStore, notifierStore } from 'src/stores'
import { ServiceActionsTable } from 'src/components/EntityTable'

import './applicationForm.scss'

class ApplicationForm extends BaseForm {
    pages = [
        {
            renderMethod: this.renderForm,
            validationSchema: yup.object().shape({
                name: yup
                    .string()
                    .matches(/^.[a-zA-Z0-9_ ]+$/, {
                        message: 'Please insert alphanumeric characters only',
                        excludeEmptyString: true,
                    })
                    .max(255, 'Please insert less than 255 characters')
                    .required('Please insert the name of the application'),
                key: yup
                    .string()
                    .matches(/^.[a-zA-Z0-9_ ]+$/, {
                        message: 'Please insert alphanumeric characters only',
                        excludeEmptyString: true,
                    })
                    .max(255, 'Please insert less than 255 characters')
                    .required('Please insert the key of the service action'),
                email: yup
                    .string()
                    .email('Must be a valid email address')
                    .max(255, 'Please insert less than 255 characters')
                    .required('Please fill in the contact email'),
                description: yup
                    .string()
                    .matches(/^.[a-zA-Z0-9 ]+$/, {
                        message: 'Please insert alphanumeric characters only',
                        excludeEmptyString: true,
                    })
                    .required('Please add a short description'),
            }),
        },
    ]

    initialValues = {
        name: '',
        key: '',
        email: '',
        description: '',
    }

    authClient = new AuthApi(authenticatedUserStore.authToken)

    constructor(props, context) {
        super(props, context)
        this.state.values = {
            ...this.initialValues,
            applicationCreated: null,
            service: null,
            serviceActionsView: true,
        }
        this.state = {
            ...this.state,
            serviceActions: [],
            page: 1,
            per_page: 10,
            loading: false,
            totalPages: 1,
            params: {},
        }
        notifierStore.setShowMessage(false)
        if (props.match.params.id) {
            this.authClient
                .fetchApplication(props.match.params.id)
                .then(response => {
                    this.setState({
                        values: {
                            ...this.state.values,
                            name: response?.data?.name,
                            key: response?.data?.key,
                            email: response?.data?.contact_email,
                            description: response?.data?.description,
                        },
                    })
                })
            this.fetchAndStoreServiceActions()
        }
    }

    componentWillUnmount() {
        this.authClient.cancelRequest()
    }

    generateGeneralInfoPayload = () => {
        const values = this.state.values
        return {
            name: values.name,
            key: values.key,
            contact_email: values.email,
            description: values.description,
        }
    }

    submitGeneralInfo = async () => {
        let isValid = await this.validateForm()
        if (isValid) {
            const payload = this.generateGeneralInfoPayload()
            if (!payload) {
                console.warn('No data to submit')
                return
            }
            notifierStore.setShowMessage(false)
            try {
                let resp = await axios({
                    url: this.getSubmitUrl(),
                    method: this.getSubmitMethod(),
                    data: payload,
                })
                if (resp.status === 200) {
                    notifierStore.setToastInfo(
                        this.props.match.params.id
                            ? 'Application data successfully changed'
                            : 'Application successfully created',
                        true
                    )
                    this.setState({
                        applicationCreated: resp.data.id,
                    })
                } else {
                    notifierStore.setToastInfo(
                        this.props.match.params.id
                            ? 'Application could not be updated'
                            : 'Application could not be created',
                        false
                    )
                }
            } catch (ex) {
                console.warn('API Exception', ex)
                notifierStore.setToastInfo(
                    ex.response?.data?.error || ex.response?.data?.detail,
                    false
                )
            } finally {
                notifierStore.setShowMessage(true)
            }
        } else {
            this.activateFirstPageWithErrors()
        }
    }

    generateServiceActionsPayload = () => {
        const values = this.state.values
        return {
            ids: values.serviceActions.map(item => item.id),
        }
    }

    submitServiceActions = async () => {
        let isValid = await this.validateForm()
        let applicationId =
            this.state.applicationCreated || this.props.match.params.id
        if (isValid) {
            const payload = this.generateServiceActionsPayload()
            if (!payload) {
                console.warn('No data to submit')
                return
            }
            notifierStore.setShowMessage(false)
            try {
                let resp = await axios({
                    url: `/api/auth/applications/${applicationId}/actions`,
                    method: 'POST',
                    data: payload,
                })
                if (resp.status === 200) {
                    notifierStore.setToastInfo(
                        'Service action(s) successfully linked to application',
                        true
                    )
                    this.setState({
                        applicationCreated: resp.data.id,
                    })
                } else {
                    notifierStore.setToastInfo(
                        'Service action(s) could not be linked to application',
                        false
                    )
                }
            } catch (ex) {
                console.warn('API Exception', ex)
                notifierStore.setToastInfo(
                    ex.response?.data?.error || ex.response?.data?.detail,
                    false
                )
            } finally {
                notifierStore.setShowMessage(true)
                this.fetchAndStoreServiceActions()
                this.setState({
                    values: {
                        ...this.state.values,
                        serviceActions: [],
                        addServiceActionsView: false,
                        service: null,
                    },
                })
            }
        } else {
            this.activateFirstPageWithErrors()
        }
    }

    getSubmitUrl() {
        if (this.props.match.params.id) {
            return `/api/auth/applications/${this.props.match.params.id}`
        }
        return '/api/auth/applications'
    }

    getSubmitMethod() {
        if (this.props.match.params.id) {
            return 'PUT'
        }
        return 'POST'
    }

    getSuccessMessage() {
        if (this.props.match.params.id) {
            return 'Application successfully updated'
        }
        return 'Application successfully created'
    }

    getFailureMessage() {
        if (this.props.match.params.id) {
            return 'Application could not be modified'
        }
        return 'Application could not be created'
    }

    // overwrite this in order to render the buttons separately
    renderFormActions(pageNumber) {
        return null
    }

    handleServiceRender = response => {
        return response.data.results
            .sort((itemA, itemB) => {
                return itemA.name.localeCompare(itemB.name)
            })
            .map(item => ({
                id: item.id,
                value: item.namespace,
                label: `${item.name}: ${item.namespace}`,
            }))
    }

    handleServiceActionsRender = response => {
        return response.data.results
            .sort((itemA, itemB) => {
                return itemA.category.localeCompare(itemB.category)
            })
            .map(item => ({
                id: item.id,
                value: item.key,
                label: `${item.service_namespace}:${item.category}:${item.key}`,
            }))
    }

    fetchAndStoreServiceActions = (
        page = this.state.page,
        per_page = this.state.per_page
    ) => {
        let applicationId =
            this.state.applicationCreated || this.props.match.params.id
        this.state = {
            ...this.state,
            loading: true,
        }
        this.authClient
            .fetchApplicationServiceActions(applicationId, page, per_page, {
                ...this.state.params,
                load_relationships: true,
            })
            .then(response => {
                this.setState({
                    serviceActions: response?.data?.results,
                    totalPages: response?.data?._page.total_pages,
                    loading: false,
                })
            })
    }

    handlePageChange = page => {
        this.setState({ page: page }, () => this.fetchAndStoreServiceActions())
    }

    deleteServiceAction = serviceActionId => {
        this.authClient
            .deleteApplicationServiceAction(
                this.props.match.params.id,
                serviceActionId
            )
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.fetchAndStoreServiceActions()
                } else {
                }
            })
            .catch(err => {})
    }

    setParams = params => {
        this.setState(
            {
                params: params,
            },
            () => this.fetchAndStoreServiceActions()
        )
    }

    renderForm() {
        return (
            <>
                <Fieldset title="Application info">
                    <Grid.Row>
                        <Grid.Col>
                            <FormGroup>
                                <Input
                                    {...this.getCommonInputProps('name')}
                                    label="Name"
                                    required
                                />
                            </FormGroup>
                        </Grid.Col>
                        <Grid.Col>
                            <FormGroup>
                                <Input
                                    {...this.getCommonInputProps('key')}
                                    label="Key"
                                    required
                                />
                            </FormGroup>
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Col lg={6}>
                            <FormGroup>
                                <Input
                                    {...this.getCommonInputProps('email')}
                                    label="Contact Email"
                                    required
                                />
                            </FormGroup>
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Col lg={6}>
                            <FormGroup>
                                <Textarea
                                    {...this.getCommonInputProps('description')}
                                    label="Description"
                                    required
                                />
                            </FormGroup>
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Col>
                            <Button
                                className={'mb-3'}
                                size={'sm'}
                                key="submit"
                                variant="secondary"
                                onClick={this.submitGeneralInfo}
                                disabled={
                                    this.state.applicationCreated &&
                                    !this.props.match.params.id
                                }
                            >
                                Submit
                            </Button>
                        </Grid.Col>
                    </Grid.Row>
                </Fieldset>
                <Fieldset title="Service actions">
                    <Info
                        info={
                            'You need to first submit the general info in order to be able to link a service action to an application'
                        }
                    />
                    {(this.state.applicationCreated ||
                        this.props.match.params.id) && (
                        <Grid.Row>
                            <div className={'add-service-action-button'}>
                                <Button
                                    variant="naked"
                                    onClick={() => {
                                        this.setState({
                                            values: {
                                                ...this.state.values,
                                                addServiceActionsView: true,
                                            },
                                        })
                                    }}
                                >
                                    <Icon icon="add" />
                                    <span>Add service action</span>
                                </Button>
                            </div>
                        </Grid.Row>
                    )}
                    {this.state.values.addServiceActionsView ? (
                        <>
                            <Grid.Row>
                                <Grid.Col lg={6}>
                                    <FormGroup>
                                        <AsyncSelect
                                            {...this.getCommonSelectProps(
                                                'service'
                                            )}
                                            apiConfig={{
                                                url: '/api/auth/services',
                                                params: inputValue => ({
                                                    page: 1,
                                                    name_like: inputValue,
                                                }),
                                            }}
                                            itemRenderer={
                                                this.handleServiceRender
                                            }
                                            label="Namespace"
                                            required
                                            search
                                        />
                                        <Info
                                            info={
                                                'You need to first select the service and namespace you want to link actions from'
                                            }
                                        />
                                    </FormGroup>
                                </Grid.Col>
                                {this.state.values.service && (
                                    <Grid.Col>
                                        <FormGroup>
                                            <AsyncSelect
                                                {...this.getCommonSelectProps(
                                                    'serviceActions'
                                                )}
                                                apiConfig={{
                                                    url: `/api/auth/services/${this.state.values?.service?.id}/actions`,
                                                    params: inputValue => ({
                                                        page: 1,
                                                        category_like:
                                                            inputValue,
                                                    }),
                                                }}
                                                itemRenderer={
                                                    this
                                                        .handleServiceActionsRender
                                                }
                                                label="Service Actions"
                                                required
                                                isMulti
                                                search
                                            />
                                        </FormGroup>
                                    </Grid.Col>
                                )}
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Col>
                                    <Button
                                        className={'mb-3'}
                                        size={'sm'}
                                        key="submit"
                                        variant="secondary"
                                        onClick={this.submitServiceActions}
                                    >
                                        Submit
                                    </Button>
                                </Grid.Col>
                            </Grid.Row>
                        </>
                    ) : null}
                    <div className="data-table-container form-data-table">
                        <ServiceActionsTable
                            {...this.props}
                            data={this.state.serviceActions}
                            loading={this.state.loading}
                            params={this.state.params}
                            setParams={this.setParams}
                            deleteAction={this.deleteServiceAction}
                            canDelete={true}
                            canEdit={false}
                            displayNamespace
                        />

                        {this.state.totalPages >= 2 && (
                            <div className="pagination-wrapper">
                                <Pagination
                                    lastPage={this.state.totalPages}
                                    currentPage={this.state.page}
                                    onClick={
                                        this.state.loading
                                            ? () => {}
                                            : this.handlePageChange
                                    }
                                />
                            </div>
                        )}
                    </div>
                </Fieldset>
            </>
        )
    }
}

export default ApplicationForm
